import React, { useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import { useMap } from './MapLibreMap';

interface MapLibreMarkerProps {
  longitude: number;
  latitude: number;
  anchor?: string;
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
}

/**
 * Renders a MapLibre marker. The marker element is created ONCE and then
 * updated in place (position via setLngLat, click via a ref). This avoids
 * tearing down and rebuilding the DOM element + GL marker on every parent
 * re-render — which, combined with the map's continuous move/zoom events,
 * previously thrashed memory and crashed constrained browsers (Tesla).
 */
const MapLibreMarker: React.FC<MapLibreMarkerProps> = ({
  longitude,
  latitude,
  anchor = 'bottom',
  children,
  onClick
}) => {
  const map = useMap();
  const markerRef = useRef<maplibregl.Marker | null>(null);
  const elementRef = useRef<HTMLDivElement>(null);

  // Keep the latest onClick without recreating the marker when its identity
  // changes (inline arrow handlers get a new identity on every render).
  const onClickRef = useRef(onClick);
  onClickRef.current = onClick;

  // Create the marker exactly once (per map instance).
  useEffect(() => {
    if (!map || markerRef.current || !elementRef.current) return;

    const el = document.createElement('div');
    el.className = 'maplibre-marker';
    el.style.pointerEvents = 'auto';
    el.style.cursor = 'pointer';

    if (elementRef.current.children.length > 0) {
      el.appendChild(elementRef.current.children[0].cloneNode(true));
    }

    markerRef.current = new maplibregl.Marker({
      element: el,
      anchor: anchor as any
    })
      .setLngLat([longitude, latitude])
      .addTo(map);

    // Stable listener that always calls the latest onClick via the ref.
    el.addEventListener('click', (e) => {
      if (onClickRef.current) {
        onClickRef.current(e as any);
      }
    });

    return () => {
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
    };
    // Intentionally only depends on `map` — position/onClick/anchor are
    // updated in place below, not by recreating the marker.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  // Update position in place when coordinates change (no rebuild).
  useEffect(() => {
    if (markerRef.current) {
      markerRef.current.setLngLat([longitude, latitude]);
    }
  }, [longitude, latitude]);

  // Keep the rendered marker content in sync (e.g. selected-state styling).
  useEffect(() => {
    const markerEl = markerRef.current?.getElement();
    const source = elementRef.current?.children[0];
    if (markerEl && source) {
      markerEl.replaceChildren(source.cloneNode(true));
    }
  });

  return (
    <div ref={elementRef} style={{ display: 'none' }}>
      {children}
    </div>
  );
};

export default MapLibreMarker;
